'use client';
// @axon/ui - Shared React components and design system

// Export provider
export * from './provider';

// Export design tokens
export * from './tokens';

// Export theme configuration
export * from './theme';
export { colorPalettes, logisticsColors, statusColors } from './theme/color-palette';

// Export all Chakra UI components
// RULE 15: Import Card and Combobox separately to preserve compound object structure
import { Card as CardComp, Combobox as ComboboxComp } from './components/index-chakra';
export const Card = CardComp;
export const Combobox: typeof ComboboxComp = ComboboxComp;
export * from './components/index-chakra';

// Export unified Combobox component (our custom implementation with search, virtual scroll, multi-select)
export { Combobox as UnifiedCombobox, ComboboxField } from './components/combobox';
export type { ComboboxOption, ComboboxProps, ComboboxFieldProps } from './components/combobox';

// Export all hooks (with specific exports to avoid conflicts)
export {
  useResponsiveValue,
  useDeviceType,
  useBreakpointValue,
  useDebounce,
  type Breakpoint as ResponsiveBreakpoint,
} from './hooks';

// Export form and disclosure hooks
export { useFormPersist, useConditionalFields, useDisclosure, useZodValidation } from './hooks';

// Export network status hook for PWA functionality
export { useNetworkStatus } from './hooks/use-network-status';
export type {
  NetworkStatus,
  NetworkStatusOptions,
  NetworkStatusHook,
  ConnectionQuality,
} from './types/network';

// Export confirm dialog hooks and provider
export { ConfirmProvider, useConfirm, useStandaloneConfirm } from './hooks/use-confirm';

// Export all utilities
export * from './lib';
export * from './utils';
// Direction already exported from utils

// Export validation utilities
export {
  validationSchemas,
  validators,
  fieldBuilders,
  createFormSchema,
  commonSchemas,
  validationMessages,
  zodHelpers,
  zodResolver,
} from './utils/validation';

// Export i18n validation utilities
export {
  createFieldBuilders,
  createCommonSchemas,
  defaultMessages,
  type ValidationMessages,
} from './utils/validation-i18n';

export { enMessages } from './utils/validation-messages-en';
export { useValidationI18n } from './hooks/use-validation-i18n';

// Export contexts
export * from './contexts';

// Export providers
export * from './providers/rtl-provider';

// Export lazy-loaded components
export * from './components/lazy';

// Export optimized components
export { DataTableOptimized } from './components/data-table-optimized';
export {
  OptimizedFormField,
  FastFormField,
  ValidationOnlyField,
} from './components/form-fields/optimized-form-field';

// Export date picker components
export { DatePickerDialog } from './components/date-picker/DatePickerDialog';
export { QuickPresets } from './components/date-picker/QuickPresets';
export * from './components/date-picker';

// Popover (Radix-backed) — used by grid cell renderers for on-click detail UX
export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverClose,
} from './components/popover';
export type { PopoverProps, PopoverContentProps } from './components/popover';

// Export navigation components
export {
  mvpNavigationItems,
  findNavigationItemByPath,
  getBreadcrumbItems,
  getLocalizedNavigationItems,
  type NavigationItem,
} from './components/navigation-config-mvp';

export { MobileNavigationBar } from './components/mobile-navigation-bar';

// Export CRUD components
export {
  ResourceForm,
  ResourceDetail,
  DeleteConfirmationDialog,
  ValidatedField,
  type DetailSection,
  type RelatedItem,
  type ResourceDetailTab,
  type FormSection as FormSectionConfig,
  type FormFieldConfig,
} from './components/crud';

// Export Form Layout components
export { FormSection, FormActions } from './components/form-layout';
export type { FormSectionProps, FormActionsProps } from './components/form-layout';

// Export EmptyState component
export { EmptyState, type EmptyStateProps, type EmptyStateVariant } from './components/empty-state';

// Export ErrorState component
export { ErrorState, type ErrorStateProps, type ErrorStateVariant } from './components/error-state';

// Export NetworkStatusBanner component
export {
  NetworkStatusBanner,
  type NetworkStatusBannerProps,
} from './components/network-status-banner';

// Export ErrorAlert components
export {
  ErrorAlert,
  ErrorAlertCompact,
  type ErrorAlertProps,
  type ErrorAlertCompactProps,
} from './components/error-alert';

// Export Form Field component for SSR-safe form validation
export { Field } from './components/form';

// Export NativeSelect component
export { NativeSelect } from './components/native-select';

// Export Enhanced Form Modal System
export {
  EnhancedFormModal,
  MOBILE_MODAL_BODY_HEIGHT,
  QuickFormModal,
  StandardFormModal,
  AdvancedFormModal,
  useFormModal,
  useDirtyCheck,
  useDirtyCheckWithForm,
  ValidatedInput,
  ValidatedTextarea,
} from './components/form-modal/index';
export type {
  EnhancedFormModalProps,
  ModalSize,
  ResponsiveSize,
  PreviewPosition,
  MotionPreset,
  QuickFormModalProps,
  StandardFormModalProps,
  AdvancedFormModalProps,
  UseFormModalOptions,
  UseFormModalReturn,
  UseDirtyCheckOptions,
  UseDirtyCheckReturn,
  ValidatedInputProps,
  ValidatedTextareaProps,
} from './components/form-modal/index';

// Export Portal component (renders children outside parent DOM hierarchy)
export { Portal } from './components/portal';
export type { PortalProps } from './components/portal';

// Export Professional Detail View components
export {
  ProfessionalDetailView,
  DetailHeroHeader,
  DetailQuickStats,
  DetailSection as ProfessionalDetailSection,
  DetailInfoField,
  DetailInfoGrid,
} from './components/professional-detail';
export type {
  ProfessionalDetailViewProps,
  TabConfig,
  DetailHeroHeaderProps,
  DetailQuickStatsProps,
  QuickStatItem,
  DetailSectionProps as ProfessionalDetailSectionProps,
  AccentPosition,
  SectionVariant,
  DetailInfoFieldProps,
  InfoFieldSize,
  InfoFieldLayout,
  DetailInfoGridProps,
  InfoGridField,
} from './components/professional-detail';

// Export Modal components
export { BaseModal, ConfirmModal } from './components/base-modal';
export type { BaseModalProps, ConfirmModalProps } from './components/base-modal';

// Export Dialog component (Chakra Dialog wrapper with TMS styling)
export { Dialog } from './components/modal';

// Export Tabs compound component
export {
  Tabs,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsContentGroup,
  TabsIndicator,
} from './components/tabs';
export type {
  TabsRootProps,
  TabsListProps,
  TabsTriggerProps,
  TabsContentProps,
  TabsContentGroupProps,
  TabsIndicatorProps,
} from './components/tabs';

// Export Switch component
export { Switch } from './components/switch';
export type { SwitchProps } from './components/switch';

// Export Badge component
export { Badge, getStatusColorPalette } from './components/badge';
export type { BadgeProps, BadgeColorPalette } from './components/badge';

// Export SimpleGrid component
export { SimpleGrid } from './components/simple-grid';
export type { SimpleGridProps } from './components/simple-grid';
